import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Tooltip, Typography, Stack } from '@mui/material'
import { CancelRounded, CheckCircleRounded, WarningRounded } from '@mui/icons-material'
import PropTypes from 'prop-types'

import React, { useState, useEffect } from 'react'
import createDialogTitle from '../dialogUtil'
import { useSmartTranslation } from '../../hooks/useSmartTranslation'

const SecondaryButton = (props) => (
  <Button
    disabled={props.disabled}
    onClick={props.onClick}
    variant={props.variant}
    sx={{
      border: '1px solid rgba(255, 255, 255, 0.23) !important',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08) !important'
      },
      color: 'initial',
      opacity: props.disabled ? 0.5 : 1
    }}
  >
    {props.children}
  </Button>
)

const SmartDialog = (props) => {
  const {
    message, acceptCallback, acceptCallbackArgsArray = [], renderComponent, cancelCallback, cancelCallbackArgsArray = [], closeCallback, closeCallbackArgsArray = [],
    cancel = true, accept = true, close, disableButtons = false, disableAccept = false, acceptDisabledReason = '', renderCustomButtons, scroll = 'paper', setOpen = false, title = null, autoClose = true, paperWidth, paperHeight,
    acceptLabel, cancelLabel, disablePortal = false, minWidth = '30rem', growContainerHeight = false, closeOnBackdrop = false, closeOnEscape = false,
    maxPaperWidth, maxPaperHeight, contentSx = {}
  } = props

  const [showDialog, setShowDialog] = useState(setOpen)
  const { t_ } = useSmartTranslation()
  useEffect(() => {
    setShowDialog(setOpen)
  }, [setOpen])

  const handleAccept = () => {
    if (acceptCallback && typeof acceptCallback === 'function') {
      acceptCallback(...acceptCallbackArgsArray)
    }
  }

  const handleCancel = () => {
    if (autoClose) {
      setShowDialog(false)
    }
    if (cancelCallback && typeof cancelCallback === 'function') {
      cancelCallback(...cancelCallbackArgsArray)
    }
  }

  const handleClose = () => {
    if (autoClose) {
      setShowDialog(false)
    }

    if (closeCallback && typeof closeCallback === 'function') {
      closeCallback(...closeCallbackArgsArray)
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      if (autoClose) {
        setShowDialog(false)
      }
      event.preventDefault()
    }
  }

  const renderTitle = () => {
    if (title === null) return
    if (title === 'default') return getTitleComponent(createDialogTitle({ color: 'warning', title: 'Attention', icon: WarningRounded }))
    return getTitleComponent(title)
  }

  const getTitleComponent = (titleToRender) => {
    return (
      <DialogTitle>
        <Stack spacing={1} alignItems='center' justifyContent='center'>
          {titleToRender.icon}
          {titleToRender.render}
        </Stack>
      </DialogTitle>
    )
  }

  return (
    <Dialog
      id='oi'
      open={showDialog}
      maxWidth='xl'
      scroll={scroll}
      disablePortal={disablePortal}
      onClose={(_, reason) => {
        if (!closeOnBackdrop && reason === 'backdropClick') return
        if (!closeOnEscape && reason === 'escapeKeyDown') return
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          handleClose()
        }
      }}
      PaperProps={{
        sx: theme => ({
          background: theme.palette.background.color_373636 + '!important',
          minWidth,
          maxWidth: maxPaperWidth ?? paperWidth,
          maxHeight: maxPaperHeight,
          minHeight: paperHeight,
          width: paperWidth
        })
      }}
    >
      {renderTitle()}
      <DialogContent
        dividers={scroll === 'paper' && title !== null}
        sx={{
          padding: scroll === 'paper' && title !== null ? '16px 16px 16px 16px ' : 0,
          margin: scroll === 'paper' && title !== null ? 0 : '16px 16px 0px 16px ',
          display: growContainerHeight ? 'flex' : undefined,
          ...contentSx
        }}
      >
        <Grid container alignItems='center' justifyContent='center' sx={growContainerHeight ? { display: 'flex', flexGrow: 1, alignItems: 'stretch' } : {}}>
          {message && message !== ''
            ? (
              <Grid item xs={12} sx={{ textAlign: 'center' }}>
                <Typography sx={{ margin: '2em 1em 1em 1em' }}>{message}</Typography>
              </Grid>
              )
            : null}
          <Grid item xs={12} sx={{ textAlign: 'center', display: growContainerHeight ? 'flex' : undefined, flexGrow: growContainerHeight ? 1 : undefined, alignItems: growContainerHeight ? 'stretch' : undefined }}>
            {renderComponent !== null && renderComponent !== '' ? <div style={growContainerHeight ? { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 } : {}}>{renderComponent}</div> : null}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Stack
          spacing={1}
          direction='row'
          justifyContent='left'
          alignItems='center'
        >
          {cancel
            ? (
              <SecondaryButton onClick={handleCancel} variant='outlined' disabled={disableButtons}>
                <CancelRounded sx={{ color: 'lightgrey !important', fontSize: '1.4em', marginRight: '0.3rem' }} />
                <Typography sx={{ color: 'lightgrey' }}>{t_(cancelLabel) || t_('CANCEL')}</Typography>
              </SecondaryButton>
              )
            : null}
          {accept
            ? (
              // A disabled button explains nothing on its own, so a caller that blocks accepting can
              // say why (story #150, RN117.5). The span is what lets the tooltip show over a disabled
              // button at all.
              <Tooltip title={disableAccept ? (acceptDisabledReason || '') : ''}>
                <span>
                  <Button color='primary' onClick={handleAccept} onKeyPress={handleKeyDown} variant='outlined' disabled={disableButtons || disableAccept}>
                    <Typography> {t_(acceptLabel) || t_('ACCEPT')} </Typography>
                    <CheckCircleRounded color={!(disableButtons || disableAccept) ? 'primary' : ''} sx={{ fontSize: '1.4em', marginLeft: '0.3rem' }} />
                  </Button>
                </span>
              </Tooltip>
              )
            : null}
          {renderCustomButtons !== undefined ? <>{renderCustomButtons}</> : null}
          {close
            ? (
              <SecondaryButton onClick={handleClose} variant='outlined' disabled={disableButtons}>
                <Typography sx={{ color: 'lightgrey !important' }}>{t_('Close')}</Typography>
                <CancelRounded sx={{ color: 'lightgrey', fontSize: '1.4em', marginLeft: '0.3rem' }} />
              </SecondaryButton>
              )
            : null}
        </Stack>
      </DialogActions>
    </Dialog>
  )
}

SmartDialog.propTypes = {
  /**
   * Boolean variable that will control components visibility.
   */
  setOpen: PropTypes.bool.isRequired,
  /**
   * Dialog title. createDialogTitle type object
   */
  title: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  /**
   * Disables ONLY the accept button, leaving cancel and close alive. For dialogs whose form is not
   * complete yet: `disableButtons` would also lock the way out.
   */
  disableAccept: PropTypes.bool,
  /**
   * Method that will be executed after pressing accept button
   */
  acceptCallback: PropTypes.func,
  /**
   * Message that will be shown on the dialog
   */
  message: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  /**
   * Arguments that will be used on acceptCallback
   */
  acceptCallbackArgsArray: PropTypes.array,
  /**
   * Component that will be loaded on the dialog
   */
  renderComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  /**
   * Method that will be executed after pressing cancel button
   */
  cancelCallback: PropTypes.func,
  /**
   * Arguments that will be used on cancelCallback
   */
  cancelCallbackArgsArray: PropTypes.array,
  /**
   * Method that will be executed after pressing close button
   */
  closeCallback: PropTypes.func,
  /**
   * Arguments that will be used on closeCallback
   */
  closeCallbackArgsArray: PropTypes.array,
  /**
   * Boolean variable that will control cancel button visibility.
   */
  cancel: PropTypes.bool,
  /**
   * Boolean variable that will control accept button visibility.
   */
  accept: PropTypes.bool,
  /**
   * Boolean variable that will control close button visibility.
   */
  close: PropTypes.bool,
  /**
   * Custom buttons that will be loaded on the dialog
   */
  renderCustomButtons: PropTypes.element,
  /**
   * Dialog scroll type. 'paper' |'body'
   */
  scroll: PropTypes.oneOf(['paper', 'body']),
  /**
   * The dialog will close on close or cancel
   */
  autoClose: PropTypes.bool,

  paperWidth: PropTypes.string,
  closeOnBackdrop: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  /**
   * Maximum width the dialog can grow to while sizing itself to its content (RN12.1).
   * Unlike paperWidth (which also forces a fixed width), this only caps the growth.
   */
  maxPaperWidth: PropTypes.string,
  /**
   * Maximum height the dialog can grow to while sizing itself to its content (RN12.1).
   */
  maxPaperHeight: PropTypes.string,
  /**
   * Extra styles merged into the DialogContent (the scroll container). Lets a caller drop the
   * default 16px top padding, which Chrome subtracts from the sticky area of any sticky child.
   */
  contentSx: PropTypes.object
}

export default SmartDialog
